import { useMemo, useEffect, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts'
import { List } from 'lucide-react'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import PptDialogList from './PptDialogList'
import { type AmbitoId } from '../../data/ambitiStats'

interface SchedaPpt {
  id: number
  area: string
  titolo: string
  descrizione: string
  indicatori: string
  azioni: string
  percentuale: string
}

const AREA_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
  '#e11d48', '#7c3aed', '#0ea5e9', '#facc15', '#a855f7',
  '#22d3ee', '#fb923c', '#4ade80', '#f43f5e', '#818cf8',
  '#2dd4bf',
]

const AREA_DESCRIPTIONS: Record<string, string> = {
  'Rete Territoriale e Offerta Sociosanitaria': 'Sviluppo e consolidamento della rete dei servizi territoriali e dell\'offerta sociosanitaria integrata.',
  'Partecipazione della Comunità e del Terzo Settore': 'Coinvolgimento attivo della comunità e delle organizzazioni del terzo settore nella co-progettazione dei servizi.',
  'Integrazione Sociosanitaria-Sociale': 'Azioni per favorire l\'integrazione tra il sistema sociosanitario e quello sociale nei percorsi di cura.',
  'Valutazione Multidimensionale': 'Strumenti e processi per la valutazione multidimensionale dei bisogni degli assistiti.',
  'Continuità dell\'Assistenza': 'Garantire la continuità dei percorsi assistenziali tra ospedale e territorio.',
  'Lo Sviluppo delle Cure Domiciliari': 'Potenziamento dei servizi di cura domiciliare per rispondere ai bisogni di prossimità.',
  'La Presa in Carico dei Cronici e dei Fragili': 'Modelli di presa in carico proattiva per pazienti cronici e soggetti fragili.',
  'Sviluppo Telemedicina': 'Introduzione e diffusione di servizi di telemedicina per il monitoraggio e la consulenza a distanza.',
  'Area Materno Infantile': 'Interventi dedicati alla salute materno-infantile e al supporto alle famiglie.',
  'Area Salute Mentale': 'Servizi e programmi per la promozione della salute mentale e la gestione delle patologie psichiatriche.',
  'Psicologia delle Cure Primarie e di Comunità e delle Cure Primarie': 'Integrazione della psicologia nei servizi di cure primarie e nella comunità.',
  'Prevenzione e Promozione della Salute': 'Programmi di prevenzione primaria e promozione di stili di vita sani sul territorio.',
  'Progetti Trasversali': 'Iniziative trasversali che coinvolgono più aree tematiche e livelli di assistenza.',
}

const AREA_AXIS_MAX_CHARS = 16
const AREA_AXIS_LABEL_WIDTH = 100

function wrapAreaLabel(value: string): string[] {
  const clean = value.trim()
  if (!clean) return ['']
  const words = clean.split(/\s+/)
  const lines: string[] = []
  let currentLine = ''
  for (const word of words) {
    const candidate = currentLine ? `${currentLine} ${word}` : word
    if (candidate.length > AREA_AXIS_MAX_CHARS && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = candidate
    }
  }
  if (currentLine) lines.push(currentLine)
  return lines
}

function AreaAxisTick({ y = 0, payload }: { y?: number; payload?: { value?: string | number } }) {
  const lines = wrapAreaLabel(String(payload?.value ?? ''))
  return (
    <text x={0} y={y} fill="var(--text-2)" fontSize={10} textAnchor="start">
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 4 : 11}>
          {line}
        </tspan>
      ))}
    </text>
  )
}

export default function PoloTerritorialeSection({
  selectedAmbitoId,
  onSelectedAmbitoChange,
}: {
  selectedAmbitoId: AmbitoId | null
  onSelectedAmbitoChange: (ambitoId: AmbitoId | null) => void
}) {
  const [schede, setSchede] = useState<SchedaPpt[]>([])

  useEffect(() => {
    fetch('/data/ppt.json')
      .then(r => r.json())
      .then((d: SchedaPpt[]) => setSchede(d))
  }, [])

  const { areaStats, totalObjectives, overallAvg, schedeByArea } = useMemo(() => {
    const map = new Map<string, { count: number; percSum: number; percCount: number }>()
    const byArea = new Map<string, SchedaPpt[]>()

    for (const s of schede) {
      if (!map.has(s.area)) {
        map.set(s.area, { count: 0, percSum: 0, percCount: 0 })
        byArea.set(s.area, [])
      }
      const entry = map.get(s.area)!
      entry.count++
      byArea.get(s.area)!.push(s)
      const p = parseFloat(s.percentuale)
      if (!isNaN(p)) {
        entry.percSum += p
        entry.percCount++
      }
    }

    let totalPerc = 0
    let totalPercCount = 0
    const stats = Array.from(map.entries()).map(([area, data], i) => {
      totalPerc += data.percSum
      totalPercCount += data.percCount
      return {
        area,
        count: data.count,
        avgPerc: data.percCount > 0 ? Math.round((data.percSum / data.percCount) * 10) / 10 : 0,
        color: AREA_COLORS[i % AREA_COLORS.length],
      }
    })

    return {
      areaStats: stats,
      totalObjectives: schede.length,
      overallAvg: totalPercCount > 0 ? Math.round((totalPerc / totalPercCount) * 10) / 10 : 0,
      schedeByArea: byArea,
    }
  }, [schede])

  const areaChartHeight = useMemo(() => {
    return areaStats.reduce((acc, s) => {
      const lines = wrapAreaLabel(s.area).length
      return acc + Math.max(34, lines * 12 + 14)
    }, 0)
  }, [areaStats])

  return (
    <>
      {/* Riga 1: 5 card */}
      <AutoGrid>
        {/* 1 — Descrizione */}
        <BSDCard id="section-ppt-desc" kind="panel" sezione="Polo Territoriale">
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>
            Piano di Sviluppo del Polo Territoriale
          </h3>
          <p style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            Il <strong>Piano di Sviluppo del Polo Territoriale (PPT)</strong> di ASST Brianza 2025–2027 definisce gli obiettivi strategici
            per il potenziamento dell'assistenza territoriale, articolati in {areaStats.length} aree tematiche.
          </p>
        </BSDCard>

        {/* 2 — Totale obiettivi (con elenco in dialog) */}
        <BSDCard
          kind="kpi"
          sezione="Polo Territoriale"
          label="Totale obiettivi"
          value={String(totalObjectives)}
          hideZoomButton
          zoomable
          sub={
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
              Tutti gli obiettivi strategici del Piano di Sviluppo, distribuiti su {areaStats.length} aree tematiche.
            </p>
          }
          zoomSub={<PptDialogList items={schede} areaColor="#6366f1" />}
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{
                width: '100%', padding: '7px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontSize: 11, fontWeight: 600, color: 'var(--text-2)', background: 'none', border: 'none',
                borderTop: '1px solid var(--border)', cursor: 'pointer', marginTop: 'auto',
              }}
            >
              <List size={14} /> Vedi elenco completo
            </button>
          )}
        />

        {/* 3 — Aree tematiche con istogramma */}
        <BSDCard
          kind="panel"
          zoomable
          sezione="Polo Territoriale"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
        >
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>
            Aree tematiche <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-3)' }}>({areaStats.length})</span>
          </h3>
          <div style={{ flex: 1, minHeight: 0, maxHeight: 180, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
            <ResponsiveContainer width="100%" height={areaChartHeight}>
              <BarChart data={[...areaStats].sort((a, b) => b.count - a.count)} layout="vertical" margin={{ top: 4, right: 22, bottom: 4, left: 6 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="area" tick={<AreaAxisTick />} axisLine={false} tickLine={false} width={AREA_AXIS_LABEL_WIDTH} />
                <Tooltip
                  contentStyle={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 8, fontSize: 12, color: 'var(--tooltip-text)' }}
                  formatter={(value: number | string | undefined) => [Number(value ?? 0), 'Obiettivi']}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={14}>
                  <LabelList dataKey="count" position="right" fill="var(--text-2)" fontSize={11} />
                  {[...areaStats].sort((a, b) => b.count - a.count).map((entry) => (<Cell key={entry.area} fill={entry.color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BSDCard>

        {/* 4 — Media realizzazione */}
        <BSDCard
          kind="kpi"
          sezione="Polo Territoriale"
          label="Media realizzazione"
          value={`${overallAvg}%`}
          sub="Percentuale media di avanzamento degli obiettivi del Polo Territoriale, calcolata sulla base delle percentuali di realizzazione dichiarate per ciascuna scheda obiettivo."
        />

        {/* 5 — Media realizzazione per area con grafico */}
        <BSDCard
          kind="panel"
          zoomable
          sezione="Polo Territoriale"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
        >
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>
            % Realizzazione per area
          </h3>
          <div style={{ flex: 1, minHeight: 0, maxHeight: 180, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
            <ResponsiveContainer width="100%" height={areaChartHeight}>
              <BarChart data={[...areaStats].sort((a, b) => b.avgPerc - a.avgPerc)} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 6 }}>
                <XAxis type="number" domain={[0, 100]} hide />
                <YAxis type="category" dataKey="area" tick={<AreaAxisTick />} axisLine={false} tickLine={false} width={AREA_AXIS_LABEL_WIDTH} />
                <Tooltip
                  contentStyle={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 8, fontSize: 12, color: 'var(--tooltip-text)' }}
                  formatter={(value: number | string | undefined) => [`${Number(value ?? 0)}%`, 'Realizzazione']}
                />
                <Bar dataKey="avgPerc" radius={[0, 6, 6, 0]} barSize={14}>
                  <LabelList dataKey="avgPerc" position="right" formatter={(v: unknown) => `${v}%`} fill="var(--text-2)" fontSize={11} />
                  {[...areaStats].sort((a, b) => b.avgPerc - a.avgPerc).map((entry) => (<Cell key={entry.area} fill={entry.color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BSDCard>
      </AutoGrid>

      {/* Card per area con elenco obiettivi */}
      <AutoGrid>
        {areaStats.map((stat) => {
          const items = schedeByArea.get(stat.area) || []
          return (
            <BSDCard
              key={stat.area}
              kind="kpi"
              sezione="Polo Territoriale"
              label={stat.area}
              value={String(items.length)}
              color={stat.color}
              hideZoomButton
              zoomable
              sub={
                <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                  {AREA_DESCRIPTIONS[stat.area] ?? `Obiettivi strategici dell'area ${stat.area}.`}
                </p>
              }
              zoomSub={<PptDialogList items={items} areaColor={stat.color} />}
              footer={(openZoom) => (
                <button
                  type="button"
                  onClick={openZoom}
                  style={{
                    width: '100%', padding: '7px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    fontSize: 11, fontWeight: 600, color: 'var(--text-2)', background: 'none', border: 'none',
                    borderTop: '1px solid var(--border)', cursor: 'pointer', marginTop: 'auto',
                  }}
                >
                  <List size={14} /> Vedi elenco completo
                </button>
              )}
            />
          )
        })}
      </AutoGrid>
    </>
  )
}
